// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import LoaderSpinner from 'components/atoms/LoaderSpinner';
import MenuGroup from 'components/atoms/MenuGroup';
import { Tooltip } from 'react-tooltip';
import { bindActionCreators, Dispatch } from 'redux';
import { trackByDate, trackByVessel } from 'services/tracks';

import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import {
    setLayoutAction,
    SetLayoutAction,
    SetMenuControlsAction,
    setMenuControlsAction,
} from 'store/actions/layoutActions';
import {
    EnviroDisplayOrderType,
    EnviroDisplayType,
    InterfaceVariablesType,
    Layout,
    OpenModal,
    PortShipType,
    PortType,
    PortURLType,
    RootState,
    SwitchType,
    TrackType,
    TransitDataType,
    TransitsDataType,
    TransitType,
    UserType,
    VesselCategoryType,
} from 'types';
import {
    GetBestPositionInterface,
    GetMetOceanByTimeInterface,
    GetTrackInterface,
    MainControlsInterface,
    MapControlsInterface,
    MetOceanChartControlsInterface,
} from 'types/functions';
import { MetOceanData } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { findNearestPoint } from 'utils/drawing';
import { checkDifferent, checkLayoutDifferent, clone, getId } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import { getFlagImage, getFlagName } from 'utils/getVesselInfo';
import {
    findBestData,
    flatTrackDate,
    formatLiveAndTimeTracksData,
    formatLiveAndTimeVesselsData,
    formatTracks,
    isVesselMoving,
} from 'utils/migrations';
import { hasPermission } from 'utils/permission';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PlaybackTransitTabComponentProps {
    active: boolean;
    layout: Layout;
    port: PortType;
    setErrorHandlerAction: SetErrorHandlerAction;

    openModelFunction: OpenModal;
    dateTimeFormat: DateTimeFormat;
    setLayoutAction: SetLayoutAction;
    showMessageAction: ShowMessageAction;
    mainControls: MainControlsInterface;
    mapControls: MapControlsInterface;
    setMenuControlsAction: SetMenuControlsAction;
    metOceanChartControls: MetOceanChartControlsInterface;
    switchs: SwitchType;
    user: UserType;
    vesselCategories: VesselCategoryType[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PlaybackTransitTabComponentState {
    active: boolean;
    layout: Layout;
    showTransitData: boolean;

    playbackId: string;
    interfaceVariables: InterfaceVariablesType;
    minimumLength: number;
    portShips?: PortShipType[];
    transitShips?: PortShipType[];
    tracks: TrackType[];
    playbackTransitId?: number | string | null;
    selectedTime?: DateTimeFormat | null | undefined;
    transit?: TransitType;
    transitData: TransitDataType;
    environmentalSources: PortURLType[] | undefined;
    enviroDisplayOrder: EnviroDisplayOrderType[] | undefined;
    enviroDisplay: EnviroDisplayType | undefined;
    activeMetOcean: boolean;
    groups: {
        id: string;
        name: string;
        active: boolean;
        total: number;
        displayMoving?: boolean;
        trackLoadedMoving?: boolean;
        drawTrackMoving?: boolean;
        displayNotMoving?: boolean;
        trackLoadedNotMoving?: boolean;
        drawTrackNotMoving?: boolean;
    }[];
    loading: boolean;
    metOceanData: MetOceanData[];
    switchs: SwitchType;
}

class PlaybackTransitTab extends Component<PlaybackTransitTabComponentProps, PlaybackTransitTabComponentState> {
    geometry: any;
    constructor(props: PlaybackTransitTabComponentProps) {
        super(props);
        this.state = {
            showTransitData: false,
            layout: clone(props.layout),
            playbackId: props.layout.playbackId,

            switchs: clone(props.switchs),
            interfaceVariables: props.port.meta_data.interfaceVariables,
            portShips: props.port.port_ships,
            transitShips: props.port.transit_ships,
            minimumLength: props.port.minimum_length | 40,

            active: props.active,
            loading: true,
            tracks: [],
            activeMetOcean: false,
            enviroDisplayOrder: props.port.meta_data.enviroDisplayOrder,
            enviroDisplay: props.port.meta_data.enviroDisplay,
            environmentalSources: props.port.port_urls?.filter((x) => {
                return x.type === 'environmental';
            }),
            groups: [
                { name: 'transitVessels', id: getId(), active: true, total: 0 },
                { name: 'otherTransits', id: getId(), active: true, total: 0 },
                { name: 'portVessels', id: getId(), active: true, total: 0 },
                { name: 'otherVessels', id: getId(), active: false, total: 0 },
            ],
            transitData: {},
            metOceanData: [],
        };
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(props: PlaybackTransitTabComponentProps, state: PlaybackTransitTabComponentState) {
        if (
            props.active !== state.active ||
            checkLayoutDifferent(props.layout, state.layout, [
                'sections.playback.menus.playbackMenu',
                'playbackId',
                'playbackSelectedTime',
                'playbackTransit',
            ])
        ) {
            return {
                layout: clone(props.layout),
                active: props.active,
                playbackId: props.layout.playbackId,
            };
        }

        if (checkDifferent(props.switchs, state.switchs, true)) {
            return {
                switchs: clone(props.switchs),
            };
        }

        return null;
    }

    componentDidMount(): void {
        this.props.setMenuControlsAction({
            playback: {
                getTrack: this.getTrack,
                getTrackMainTrack: this.getTrackMainTrack,
                getBestPosition: this.getBestPosition,
                getMetOceanByTime: this.getMetOceanByTime,
                trackDisplay: this.trackDisplay,
                trackDrawTrack: this.trackDrawTrack,
            },
        });
    }

    componentDidUpdate(
        props: Readonly<PlaybackTransitTabComponentProps>,
        state: Readonly<PlaybackTransitTabComponentState>
    ): void {
        if (this.state.playbackId !== state.playbackId && this.props.active) {
            this.startPlayback();
        }

        if (checkDifferent(this.state.tracks, state.tracks)) {
            this.updateGroups();
        }
    }

    startPlayback = () => {
        // eslint-disable-next-line prefer-const
        let { transit, transitData } = this.props.mainControls.getTransit(this.state.layout.playbackTransit);
        const selectedTime = this.state.layout.playbackSelectedTime;

        if (!transit) {
            //@ts-ignore
            transit = this.state.layout.tempraryTransit;
        }
        if (!transit) {
            this.props.showMessageAction({
                messageType: 'ERROR',
                message: translate('Transit data not loadad please select other transit.'),
            });
            this.setState({
                loading: false,
            });

            this.props.setLayoutAction([
                {
                    action: 'UPDATE_SECTION',
                    data: { name: 'replay', loading: false },
                },
                {
                    action: 'UPDATE_SECTION',
                    data: { name: 'playback', loading: false },
                },
            ]);

            return;
        }
        console.log('Start playback mode', transit, selectedTime?.toIso());

        this.setState(
            {
                selectedTime,
                transit, //@ts-ignore
                transitData,
                tracks: [],
                loading: true,
            },
            () => {
                this.props.mapControls.playback?.updateTracks(
                    [],
                    //@ts-ignore
                    null,
                    this.state.transit?.start_utc,
                    this.state.transit?.end_utc,
                    transit
                );
                //@ts-ignore
                this.loadPlaybackData(this.state.layout.playbackTransit, transit, selectedTime);
                this.getMetOceanByTime(selectedTime);
            }
        );
    };

    getMetOceanByTime: GetMetOceanByTimeInterface = async (
        time: DateTimeFormat | undefined | null
    ): Promise<MetOceanData[]> => {
        if (!time) {
            return [];
        }

        try {
            const metOceanData = await this.props.mainControls.getMetOceanData(this.state.transit, time);

            this.setState({ metOceanData: metOceanData, selectedTime: time });
            return metOceanData;
        } catch (error) {
            return [];
        }
    };

    loadPlaybackData = (
        playbackTransitId: number | string | null,
        transit: TransitType,
        selectedTime: DateTimeFormat | null
    ) => {
        console.log('Start playback mode', playbackTransitId, transit, selectedTime);
        trackByDate(this.props.port.id, {
            endDate: transit.end_utc.toIso(),
            id: playbackTransitId,
            mode: 'playback',
            startDate: transit.start_utc.toIso(),
        })
            .then((res) => {
                const vesselsData = formatLiveAndTimeTracksData(
                    res,
                    this.props.port,
                    this.props.port.backEnd?.version,
                    this.props.vesselCategories
                );
                console.log(vesselsData);
                this.setState(
                    {
                        playbackTransitId,
                        selectedTime,
                        transit,
                        tracks: formatTracks(
                            transit.id,
                            transit.mmsi,
                            vesselsData,
                            this.props.switchs,
                            this.state.interfaceVariables,
                            this.state.minimumLength,
                            this.state.portShips,
                            this.state.transitShips
                        ),
                    },
                    () => {
                        this.loadVesselsData(true);
                    }
                );
            })
            .catch((errors) => {
                // Handle errors
                this.props.setErrorHandlerAction(errors);
            });
    };

    updateGroups = () => {
        console.log('updateGroups');

        this.setState((prevState) => {
            const { tracks, groups } = prevState;

            const updatedGroups = groups.map((group) => {
                const groupTracks = tracks.filter((t) => t.options.group === group.name);
                const movingTracks = groupTracks.filter((t) => t.options.moving);
                const notMovingTracks = groupTracks.filter((t) => !t.options.moving);

                const computeState = (data, key) => {
                    if (data.length === 0) return false;
                    return data.every((x) => x.options[key]);
                };

                return {
                    ...group,
                    total: groupTracks.length,
                    displayMoving: computeState(movingTracks, 'display'),
                    drawTrackMoving: computeState(movingTracks, 'drawTrack'),
                    trackLoadedMoving: computeState(movingTracks, 'trackLoaded'),
                    displayNotMoving: computeState(notMovingTracks, 'display'),
                    drawTrackNotMoving: computeState(notMovingTracks, 'drawTrack'),
                    trackLoadedNotMoving: computeState(notMovingTracks, 'trackLoaded'),
                };
            });

            return { groups: updatedGroups };
        });
    };

    getBestPosition: GetBestPositionInterface = (id, positionId, target, dataType) => {
        const track = this.getTrack(id);
        if (!track) {
            return;
        }
        const { data, type } = findBestData(track?.data, null, null, dataType);
        if (positionId) {
            const position = data.find((x) => {
                return x.id === positionId;
            });
            if (position) {
                return position;
            }
        }
        return findNearestPoint(data, target);
    };

    getTrack: GetTrackInterface = (id: string | number) => {
        return this.state.tracks.find((x) => {
            return x.uuid === id;
        });
    };

    getTrackMainTrack = (): TrackType | undefined => {
        //TODO we need the vessel id from the back-end
        // AND remove this line
        if (this.props.port.backEnd?.version === 2) {
            return this.state.tracks.find((x) => {
                return x.vessel.mmsi === this.state.transit?.vessel.mmsi;
            });
        }
        return this.state.tracks.find((x) => {
            return x.vessel_id === this.state.transit?.vessel_id;
        });
    };

    loadVesselData = async (id, mmsi) => {
        try {
            const results = await trackByVessel(this.props.port.id, id, {
                endDate: this.state.transit?.end_utc.toIso(),
                id: this.state.playbackTransitId,
                mmsi: mmsi,
                mode: 'playback',
                startDate: this.state.transit?.start_utc.toIso(),
            });
            return { results, status: true }; // Return the resolved data
        } catch (errors) {
            return { results: {}, status: false };
        }
    };

    loadVesselsData = async (updateTime: boolean) => {
        try {
            const vesselResults = await Promise.all(
                this.state.tracks.map(async (track) => {
                    if (!track.options.trackLoaded && track.options.display) {
                        const { results, status } = await this.loadVesselData(track.vessel_id, track.vessel.mmsi);
                        return {
                            track,
                            results: formatLiveAndTimeVesselsData(
                                results,
                                track.vessel_id,
                                this.props.port.backEnd?.version
                            ),
                            status,
                        };
                    }
                    return {
                        track,
                        results: null,
                        status: true,
                    };
                })
            );

            if (vesselResults.filter((r) => !r.status).length === vesselResults.length && vesselResults.length > 0) {
                this.setState({
                    tracks: this.state.tracks.map((track) => {
                        return {
                            ...track,
                            options: {
                                ...track.options,
                                loading: false,
                            },
                        };
                    }),
                });
                return;
            } else if (vesselResults.filter((r) => !r.status).length > 0 && vesselResults.length > 0) {
                this.props.openModelFunction(
                    'loadPlaybackModeVesselErrorModel',
                    0,
                    'medium',
                    {},
                    {
                        onCancel: () => {
                            this.setState({
                                loading: false,
                            });

                            this.props.openModelFunction('loadPlaybackModeVesselErrorModel');
                        },
                        onConfirm: () => {
                            this.setState({
                                loading: false,
                            });
                        },
                    },
                    true
                );
            }

            // Build merged tracks
            const mergedTracks = vesselResults.map(({ track, results }) => {
                if (!results || !results?.[`v-${track.vessel_id}`]) {
                    return {
                        ...track,
                        options: {
                            ...track.options,
                            loading: false,
                        },
                    };
                }

                const vesselData = results[`v-${track.vessel_id}`];

                const isThePlaybackVessel = track.vessel.mmsi === this.state.transit?.vessel.mmsi;

                const useTransitSmoothed = isThePlaybackVessel && !!this.state.transitData?.smoothed;

                const useTransitCentroid = isThePlaybackVessel && !!this.state.transitData?.centroid;

                const useTransitRawAIS = isThePlaybackVessel && !!this.state.transitData?.rawAIS;

                return {
                    ...track,

                    best: isThePlaybackVessel ? this.state.transit?.best || 'rawAIS' : 'rawAIS',

                    data: {
                        events: isThePlaybackVessel ? this.state.transit?.events || [] : [],

                        offsets: isThePlaybackVessel
                            ? {
                                  ...(this.state.transitData?.offsets || {}),
                                  ...(vesselData?.offsets || {}),
                              }
                            : vesselData?.offsets || {},

                        smoothedId: useTransitSmoothed ? this.state.transitData!.smoothedId : getId(),

                        smoothed: flatTrackDate(
                            this.props.dateTimeFormat,
                            isThePlaybackVessel && this.state.transitData?.smoothed
                                ? this.state.transitData.smoothed
                                : vesselData?.smoothed?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),

                        centroidId: useTransitCentroid ? this.state.transitData!.centroidId : getId(),

                        centroid: flatTrackDate(
                            this.props.dateTimeFormat,
                            isThePlaybackVessel && this.state.transitData?.centroid
                                ? this.state.transitData.centroid
                                : vesselData?.centroid?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),

                        rawAISId: useTransitRawAIS ? this.state.transitData!.rawAISId : getId(),

                        rawAIS: flatTrackDate(
                            this.props.dateTimeFormat,
                            isThePlaybackVessel && this.state.transitData?.rawAIS
                                ? this.state.transitData.rawAIS
                                : vesselData?.rawAIS?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),
                    },

                    options: {
                        ...track.options,

                        moving: isVesselMoving(vesselData?.rawAIS?.data),

                        loading: false,
                        trackLoaded: true,
                        rawAISLoaded: true,
                        centroidLoaded: true,
                        smoothedLoaded: true,
                    },
                };
            });

            // Update UI
            this.setState({ tracks: mergedTracks }, async () => {
                await this.props.mapControls.playback?.updateTracks(
                    mergedTracks,
                    //@ts-ignore
                    updateTime ? this.state.selectedTime : null,
                    this.state.transit?.start_utc,
                    this.state.transit?.end_utc,
                    this.state.transit
                );

                this.setState({
                    loading: false,
                });

                this.props.setLayoutAction([
                    {
                        action: 'UPDATE_SECTION',
                        data: {
                            name: 'replay',
                            loading: false,
                        },
                    },
                    {
                        action: 'UPDATE_SECTION',
                        data: {
                            name: 'playback',
                            loading: false,
                        },
                    },
                ]);

                if (updateTime) {
                    setTimeout(() => {
                        const track = this.getTrackMainTrack();

                        if (track) {
                            this.props.mapControls.playback?.goToVesselLocation(
                                track.uuid,
                                false,
                                this.props.layout.viewPort?.zoom
                            );
                        }
                    }, 1000);
                }
            });
        } catch (errors) {
            this.setState({
                loading: false,
            });

            this.props.setLayoutAction([
                {
                    action: 'UPDATE_SECTION',
                    data: {
                        name: 'replay',
                        loading: false,
                    },
                },
                {
                    action: 'UPDATE_SECTION',
                    data: {
                        name: 'playback',
                        loading: false,
                    },
                },
            ]);

            this.props.openModelFunction(
                'loadPlaybackModeVesselErrorModel',
                0,
                'medium',
                {},
                {
                    onCancel: () => {
                        this.setState({
                            loading: false,
                        });

                        this.props.openModelFunction('loadPlaybackModeVesselErrorModel');
                    },
                    onConfirm: () => {
                        this.setState({
                            loading: false,
                        });
                    },
                },
                true
            );
        }
    };

    goToVesel = (uuid: string) => {
        this.props.mapControls.playback?.goToVesselLocation(uuid);
    };

    trackDisplay = async (uuid: string, value) => {
        await this.updateOption(uuid, 'display', value);
    };

    trackDrawTrack = async (uuid: string, value) => {
        await this.updateOption(uuid, 'drawTrack', value);
    };

    trackDisplayGroup = (name: string, moving: boolean | null, value: boolean) => {
        this.updateOptions(name, moving, 'display', value);
    };

    trackDrawTrackGroup = (name: string, moving: boolean | null, value) => {
        this.updateOptions(name, moving, 'drawTrack', value);
    };

    updateOptions(group: string | null, moving: boolean | null, key: string, value: boolean) {
        this.setState(
            {
                tracks: this.state.tracks.map((track) => {
                    if (
                        (track.options.group === group || group === null) &&
                        (track.options.moving === moving || moving === null)
                    ) {
                        return {
                            ...track,
                            options: {
                                ...track.options,
                                loading: key === 'display' && !track.options.trackLoaded,
                                [key]: value === null ? !track.options[key] : value,
                            },
                        };
                    }

                    return track;
                }),
            },
            () => {
                this.loadVesselsData(false);
            }
        );
    }

    async updateOption(uuid: string | null, key: string, value: boolean) {
        await new Promise<void>((resolve) => {
            this.setState(
                {
                    tracks: this.state.tracks.map((track) => {
                        if (track.uuid === uuid || uuid === null) {
                            return {
                                ...track,
                                options: {
                                    ...track.options,
                                    loading: key === 'display' && !track.options.trackLoaded,
                                    [key]: value === null ? !track.options[key] : value,
                                },
                            };
                        }

                        return track;
                    }),
                },
                () => {
                    this.loadVesselsData(false);
                    resolve();
                }
            );
        });

        // Now the state is updated and loadVesselsData has completed.
        // You can safely continue from here
    }

    updateGroupState = (value: boolean, index: number) => {
        this.setState({
            groups: this.state.groups.map((v, i) => {
                if (i === index) {
                    v.active = value;
                }
                return v;
            }),
        });
    };

    getOffsetDate = (key) => {
        //@ts-ignore
        const data = this.state.transitData[this.state.transit.best];
        //@ts-ignore
        const offset = this.state.transitData.offsets[data[0]?.vessel_offset_id];
        if (!offset) {
            return '-';
        }
        switch (key) {
            case 'heading':
                if (offset?.heading_correction?.toString()) {
                    return ` ${offset.heading_correction}  (${offset.heading.replace(' heading', '')})`;
                } else {
                    return 'N/A';
                }

            case 'lateral':
                if (offset?.heading_correction?.toString()) {
                    return `${offset.lateral_correction} (${offset.lateral.replace(' lateral', '')})`;
                } else {
                    return 'N/A';
                }

            default:
                break;
        }
    };

    toggleTransitData = () => {
        this.setState({ showTransitData: !this.state.showTransitData });
    };

    toggleMetOceanMenu = () => {
        this.setState({
            activeMetOcean: !this.state.activeMetOcean,
        });
    };

    openMetOceanChart = (type: string) => {
        //TODO
        // this.props.setLayoutAction(['UPDATE_SECTION', {
        //     tab: 'metOceanChart',
        //     menu: '',
        //     menuSection: '',
        // }]);

        // this.props.setLayoutAction('DEFAULT',
        //     'metOceanChart',
        //     ['grid', 'chart', 'analysis', 'draw', 'measure', 'menus'],
        //     ['playback', 'PlaybackTransitTab']
        // );
        setTimeout(() => {
            //@ts-ignore
            this.props.metOceanChartControls.createTransitChart(type, this.state.transit, this.state.selectedTime);
        }, 100);
    };

    render() {
        return (
            <div className={twclsx(!this.state.active && 'hidden')}>
                {this.state.loading ? (
                    <LoaderSpinner />
                ) : (
                    <>
                        <div>
                            {this.state.groups
                                .filter((group, index) => {
                                    return index === 0;
                                })
                                .map((group, index) => {
                                    return (
                                        <div className="px-2" key={group.id}>
                                            <div>
                                                {this.state.tracks
                                                    .filter((x) => {
                                                        return x.options.group === group.name;
                                                    })
                                                    .map((x) => {
                                                        return (
                                                            <div
                                                                key={x.uuid}
                                                                className="flex justify-between border-b border-gray-300 relative pb-1  raw"
                                                            >
                                                                {x.options.loading && (
                                                                    <div className="absolute z-10 text-center w-full h-full bg-gray-200 cursor-pointer  bg-opacity-25 ">
                                                                        <LoaderSpinner
                                                                            className="mt-3"
                                                                            variant="button"
                                                                        ></LoaderSpinner>
                                                                    </div>
                                                                )}
                                                                {!x.options.trackLoaded && (
                                                                    <div
                                                                        className="absolute z-10 w-full h-full bg-gray-200 cursor-pointer opacity-25 "
                                                                        onClick={() => {
                                                                            if (!x.options.trackLoaded) {
                                                                                this.trackDisplay(x.uuid, true);
                                                                            }
                                                                        }}
                                                                    ></div>
                                                                )}
                                                                <div
                                                                    onClick={async () => {
                                                                        if (!x.options.trackLoaded) {
                                                                            await this.trackDisplay(x.uuid, true);
                                                                        }
                                                                        this.goToVesel(x.uuid);
                                                                    }}
                                                                    className={twclsx(
                                                                        'cursor-pointer',
                                                                        'pt-1',

                                                                        !x.options.display && 'opacity-25'
                                                                    )}
                                                                >
                                                                    <div>
                                                                        <Image
                                                                            alt={getFlagName(x?.mmsi) || ''}
                                                                            className="float-left mt-1 mr-1"
                                                                            src={getFlagImage(x.mmsi) || ''}
                                                                        />
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.vessel.name}
                                                                        </span>
                                                                    </div>
                                                                    <div className="capitalize text-sm leading-3">
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.mmsi}
                                                                        </span>{' '}
                                                                        {translate(x.vessel.vessel_category?.label)}{' '}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-1 pt-2 pr-1">
                                                                    {/* <div>
                                                                        {x.options.display ? (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, false);
                                                                                }}
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, true);
                                                                                }}
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer">
                                                                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        onClick={() => {
                                                                            this.trackDrawTrack(
                                                                                x.uuid,
                                                                                !x.options.drawTrack
                                                                            );
                                                                        }}
                                                                    >
                                                                        <Icon
                                                                            className={twclsx(
                                                                                x.options.drawTrack && 'text-black',
                                                                                'w-4',
                                                                                'h-4',
                                                                                'cursor-pointer'
                                                                            )}
                                                                        >
                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                        </Icon>
                                                                    </div> */}
                                                                    <div
                                                                        onClick={this.toggleTransitData}
                                                                        className="cursor-pointer"
                                                                    >
                                                                        {this.state.showTransitData ? (
                                                                            <Icon
                                                                                className={twclsx(
                                                                                    'text-black',
                                                                                    'w-4',
                                                                                    'h-4',
                                                                                    'cursor-pointer'
                                                                                )}
                                                                            >
                                                                                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                                                                                <path d="M12 9h.01" />
                                                                                <path d="M11 12h1v4h1" />
                                                                            </Icon>
                                                                        ) : (
                                                                            <Icon
                                                                                className={twclsx(
                                                                                    'text-black',
                                                                                    'w-4',
                                                                                    'h-4',
                                                                                    'cursor-pointer'
                                                                                )}
                                                                            >
                                                                                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                                                                                <path d="M12 9h.01" />
                                                                                <path d="M11 12h1v4h1" />
                                                                            </Icon>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                            {this.state.showTransitData && (
                                                <div>
                                                    {this.state.transit !== null && this.state.transit?.mmsi && (
                                                        <div className="py-2">
                                                            <div className=" flex flex-col gap-3">
                                                                <div className="flex flex-col justify-start">
                                                                    <div className="flex justify-between">
                                                                        <div className="flex   justify-start gap-1 items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('Data')}:
                                                                            </span>
                                                                            <p>{translate(this.state.transit.best)}</p>
                                                                        </div>
                                                                        <div className="flex  justify-start gap-1 items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('AIS')}:
                                                                            </span>
                                                                            <p>{translate('received')}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <div className="flex  justify-start gap-1  items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('Lateral offsets')}:
                                                                            </span>
                                                                            <p>{this.getOffsetDate('lateral')}</p>
                                                                        </div>
                                                                        <div className="flex   justify-start gap-1 items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('MetOcean')}:
                                                                            </span>
                                                                            <p>
                                                                                {this.state.transit.environmental
                                                                                    ? translate('received')
                                                                                    : translate('not received')}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <div className="flex  justify-start gap-1  items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('Heading offset')}:
                                                                            </span>
                                                                            <p>{this.getOffsetDate('heading')}</p>
                                                                        </div>
                                                                        <div className="flex  justify-start gap-1 items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('Calculations')}:
                                                                            </span>
                                                                            <p>
                                                                                {this.state.transit.analysis
                                                                                    ? translate('Done')
                                                                                    : translate('not completed')}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <div className="flex  justify-start gap-1 items-center text-sm">
                                                                            <span className="text-gray-600 font-bold">
                                                                                {translate('Processed')}:
                                                                            </span>
                                                                            <p className="text-red-500">
                                                                                {this.state.transit.updated_at.formatDateTimeToString(
                                                                                    'dd MMM yyyy HH:mm:ssZ'
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                        <div>
                            {this.state.groups
                                .filter((group, index) => {
                                    return (
                                        index > 0 &&
                                        this.state.layout.sections.playback.menus.playbackMenu.sections[group.name]
                                            .state
                                    );
                                })
                                .map((group, index) => {
                                    return (
                                        <MenuGroup
                                            active={true}
                                            key={group.id}
                                            // callback={(value: boolean) => {
                                            //     this.updateGroupState(value, index);
                                            // }}
                                            showArrow={false}
                                            sticky
                                            title={`${group.total} ${translate('vessels')}`}
                                        >
                                            <div>
                                                {this.state.tracks
                                                    .filter((x) => x.options.group === group.name)
                                                    .sort((a, b) => {
                                                        if (a.vessel.name < b.vessel.name) return -1;
                                                        if (a.vessel.name > b.vessel.name) return 1;
                                                        return 0;
                                                    })

                                                    .filter((x) => x.options.moving)
                                                    .map((x, i) => {
                                                        return (
                                                            <>
                                                                {i === 0 && (
                                                                    <div className="bg-gray-100 raw relative border-b border-t border-gray-300 py-1 flex justify-between  ">
                                                                        <div className=" flex items-center gap-1">
                                                                            <div>
                                                                                <Icon>
                                                                                    <path d="M2 20a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1"></path>
                                                                                    <path d="M4 18l-1 -5h18l-2 4"></path>
                                                                                    <path d="M5 13v-6h8l4 6"></path>
                                                                                    <path d="M7 7v-4h-1"></path>
                                                                                </Icon>
                                                                            </div>
                                                                            <div className="font-bold ">
                                                                                {translate('Underway vessels')}
                                                                            </div>
                                                                        </div>
                                                                        <div className="rawActionsStaticRight hidden right-0 flex justify-items-center h-full gap-1 pt-2 pr-1">
                                                                            <div>
                                                                                {!group.displayMoving ? (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black ">
                                                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                {!group.drawTrackMoving ? (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="rawActionsRight right-0 flex justify-items-center h-full gap-1 pt-2 pr-1 pl-10">
                                                                            <div>
                                                                                {!group.displayMoving ? (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDisplayGroup(
                                                                                                group.name,
                                                                                                true,
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`displayGroupUnderwayVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                        </Icon>
                                                                                        <Tooltip
                                                                                            id="displayGroupUnderwayVessels"
                                                                                            html={`${translate(
                                                                                                'Show all vessels'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDisplayGroup(
                                                                                                group.name,
                                                                                                true,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`displayGroupUnderwayVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                        <Tooltip
                                                                                            id="displayGroupUnderwayVessels"
                                                                                            html={`${translate(
                                                                                                'Hide all vessels'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                {!group.drawTrackMoving ? (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDrawTrackGroup(
                                                                                                group.name,
                                                                                                true,
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`trackGroupUnderwayVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        </Icon>{' '}
                                                                                        <Tooltip
                                                                                            className="z-20"
                                                                                            id="trackGroupUnderwayVessels"
                                                                                            html={`${translate(
                                                                                                'Show all vessels tracks'
                                                                                            )}`}
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDrawTrackGroup(
                                                                                                group.name,
                                                                                                true,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`trackGroupUnderwayVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                        <Tooltip
                                                                                            className="z-20"
                                                                                            id="trackGroupUnderwayVessels"
                                                                                            html={`${translate(
                                                                                                'Hide all vessels tracks'
                                                                                            )}`}
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    key={x.uuid}
                                                                    className="flex justify-between border-b border-gray-300 relative pb-1  raw"
                                                                >
                                                                    {x.options.loading && (
                                                                        <div className="absolute z-10 text-center w-full h-full bg-gray-200 cursor-pointer  bg-opacity-25 ">
                                                                            <LoaderSpinner
                                                                                className="mt-3"
                                                                                variant="button"
                                                                            ></LoaderSpinner>
                                                                        </div>
                                                                    )}
                                                                    {!x.options.trackLoaded && (
                                                                        <div
                                                                            className="absolute z-10 w-full h-full bg-gray-200 cursor-pointer opacity-25 "
                                                                            onClick={() => {
                                                                                if (!x.options.trackLoaded) {
                                                                                    this.trackDisplay(x.uuid, true);
                                                                                }
                                                                            }}
                                                                        ></div>
                                                                    )}
                                                                    <div
                                                                        onClick={async () => {
                                                                            if (!x.options.trackLoaded) {
                                                                                await this.trackDisplay(x.uuid, true);
                                                                            }
                                                                            this.goToVesel(x.uuid);
                                                                        }}
                                                                        className={twclsx(
                                                                            'cursor-pointer',
                                                                            'pt-1',

                                                                            !x.options.display && 'opacity-25'
                                                                        )}
                                                                    >
                                                                        <div>
                                                                            <Image
                                                                                alt={getFlagName(x?.mmsi) || ''}
                                                                                className="float-left mt-1 mr-1"
                                                                                src={getFlagImage(x.mmsi) || ''}
                                                                            />
                                                                            <span
                                                                                className={twclsx(
                                                                                    (!hasPermission(
                                                                                        this.props.user,
                                                                                        this.props.port.id,
                                                                                        ['identifyTransits']
                                                                                    ) ||
                                                                                        !this.state.switchs
                                                                                            .identifyTransits.value) &&
                                                                                        'blurText'
                                                                                )}
                                                                            >
                                                                                {x.vessel.name}
                                                                            </span>
                                                                        </div>
                                                                        <div className="capitalize text-sm leading-3">
                                                                            <span
                                                                                className={twclsx(
                                                                                    (!hasPermission(
                                                                                        this.props.user,
                                                                                        this.props.port.id,
                                                                                        ['identifyTransits']
                                                                                    ) ||
                                                                                        !this.state.switchs
                                                                                            .identifyTransits.value) &&
                                                                                        'blurText'
                                                                                )}
                                                                            >
                                                                                {x.mmsi}
                                                                            </span>{' '}
                                                                            {translate(x.vessel.vessel_category?.label)}{' '}
                                                                        </div>
                                                                    </div>
                                                                    {
                                                                        // TODO //make the two icons at the center
                                                                        // TODO //add tooltip for this action
                                                                        // TODO // add backslash to the icon and keep make it black
                                                                    }
                                                                    <div className=" rawActionsStaticRight  no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                        <div>
                                                                            {x.options.display ? (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-gray-600 ">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-gray-400 ">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {x.options.drawTrack &&
                                                                            x.options.display ? (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer  text-gray-600">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer  text-gray-400">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className=" rawActionsRight no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                        <div>
                                                                            {!x.options.display ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplay(x.uuid, true);
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                        html={translate('show vessel')}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplay(
                                                                                            x.uuid,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                        html={translate('hide vessel')}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {x.options.drawTrack &&
                                                                            x.options.display ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrack(
                                                                                            x.uuid,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-track-tooltip`}
                                                                                        html={translate(
                                                                                            'hide vessel track'
                                                                                        )}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrack(
                                                                                            x.uuid,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-track-tooltip`}
                                                                                        html={translate(
                                                                                            'show vessel track'
                                                                                        )}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        );
                                                    })}
                                            </div>
                                            <div>
                                                {this.state.tracks
                                                    .filter((x) => x.options.group === group.name)
                                                    .sort((a, b) => {
                                                        if (a.vessel.name < b.vessel.name) return -1;
                                                        if (a.vessel.name > b.vessel.name) return 1;
                                                        return 0;
                                                    })

                                                    .filter((x) => x.options.moving === false)
                                                    .map((x, i) => {
                                                        return (
                                                            <>
                                                                {i === 0 && (
                                                                    <div className="bg-gray-100 border-b border-t raw relative border-gray-300 py-1 flex justify-between  ">
                                                                        <div className=" flex items-center gap-1">
                                                                            <div>
                                                                                <Icon>
                                                                                    <path d="M2 20a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1"></path>
                                                                                    <path d="M4 18l-1 -5h18l-2 4"></path>
                                                                                    <path d="M5 13v-6h8l4 6"></path>
                                                                                    <path d="M7 7v-4h-1"></path>
                                                                                </Icon>
                                                                            </div>
                                                                            <div className="font-bold ">
                                                                                {translate('Berthed/Anchored vessels')}
                                                                            </div>
                                                                        </div>
                                                                        <div className="rawActionsStaticRight hidden h-full right-0 flex justify-items-center gap-1 pt-2 pr-1">
                                                                            <div>
                                                                                {!group.displayNotMoving ? (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer  text-black">
                                                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                {!group.drawTrackNotMoving ? (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div>
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="rawActionsRight h-full right-0  flex justify-items-center gap-1 pt-2 pr-1 pl-10">
                                                                            <div>
                                                                                {!group.displayNotMoving ? (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDisplayGroup(
                                                                                                group.name,
                                                                                                false,
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`displayGroupBerthedVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                        </Icon>{' '}
                                                                                        <Tooltip
                                                                                            id="displayGroupBerthedVessels"
                                                                                            html={`${translate(
                                                                                                'Show all vessels'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDisplayGroup(
                                                                                                group.name,
                                                                                                false,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`displayGroupBerthedVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>{' '}
                                                                                        <Tooltip
                                                                                            id="displayGroupBerthedVessels"
                                                                                            html={`${translate(
                                                                                                'Hide all vessels'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                {!group.drawTrackNotMoving ? (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDrawTrackGroup(
                                                                                                group.name,
                                                                                                false,
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`trackGroupBerthedVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        </Icon>
                                                                                        <Tooltip
                                                                                            id="trackGroupBerthedVessels"
                                                                                            html={`${translate(
                                                                                                'Show all vessels tracks'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        onClick={() => {
                                                                                            this.trackDrawTrackGroup(
                                                                                                group.name,
                                                                                                false,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                        data-tooltip-id={`trackGroupBerthedVessels`}
                                                                                    >
                                                                                        <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                            <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                            <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                            <path d="M3 3l18 18"></path>
                                                                                        </Icon>
                                                                                        <Tooltip
                                                                                            id="trackGroupBerthedVessels"
                                                                                            html={`${translate(
                                                                                                'Show all vessels tracks'
                                                                                            )}`}
                                                                                            className="z-20"
                                                                                            place={'bottom-end'}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    key={x.uuid}
                                                                    className="flex justify-between border-b border-gray-300 relative pb-1 raw"
                                                                >
                                                                    {x.options.loading && (
                                                                        <div className="absolute z-10 text-center w-full h-full bg-gray-200 cursor-pointer  bg-opacity-25 ">
                                                                            <LoaderSpinner
                                                                                className="mt-3"
                                                                                variant="button"
                                                                            ></LoaderSpinner>
                                                                        </div>
                                                                    )}
                                                                    {!x.options.trackLoaded && (
                                                                        <div
                                                                            className="absolute z-10 w-full h-full bg-gray-200 cursor-pointer opacity-25 "
                                                                            onClick={() => {
                                                                                if (!x.options.trackLoaded) {
                                                                                    this.trackDisplay(x.uuid, true);
                                                                                }
                                                                            }}
                                                                        ></div>
                                                                    )}
                                                                    <div
                                                                        onClick={async () => {
                                                                            if (!x.options.trackLoaded) {
                                                                                await this.trackDisplay(x.uuid, true);
                                                                            }
                                                                            this.goToVesel(x.uuid);
                                                                        }}
                                                                        className={twclsx(
                                                                            'cursor-pointer',
                                                                            'pt-1',

                                                                            !x.options.display && 'opacity-25'
                                                                        )}
                                                                    >
                                                                        <div>
                                                                            <Image
                                                                                alt={getFlagName(x?.mmsi) || ''}
                                                                                className="float-left mt-1 mr-1"
                                                                                src={getFlagImage(x.mmsi) || ''}
                                                                            />
                                                                            <span
                                                                                className={twclsx(
                                                                                    (!hasPermission(
                                                                                        this.props.user,
                                                                                        this.props.port.id,
                                                                                        ['identifyTransits']
                                                                                    ) ||
                                                                                        !this.state.switchs
                                                                                            .identifyTransits.value) &&
                                                                                        'blurText'
                                                                                )}
                                                                            >
                                                                                {x.vessel.name}
                                                                            </span>
                                                                        </div>
                                                                        <div className="capitalize text-sm leading-3">
                                                                            <span
                                                                                className={twclsx(
                                                                                    (!hasPermission(
                                                                                        this.props.user,
                                                                                        this.props.port.id,
                                                                                        ['identifyTransits']
                                                                                    ) ||
                                                                                        !this.state.switchs
                                                                                            .identifyTransits.value) &&
                                                                                        'blurText'
                                                                                )}
                                                                            >
                                                                                {x.mmsi}
                                                                            </span>{' '}
                                                                            {translate(x.vessel.vessel_category.label)}{' '}
                                                                        </div>
                                                                    </div>
                                                                    {
                                                                        // TODO //make the two icons at the center
                                                                        // TODO //add tooltip for this action
                                                                        // TODO // add backslash to the icon and keep make it black
                                                                    }
                                                                    <div className=" rawActionsStaticRight  no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                        <div>
                                                                            {x.options.display ? (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-gray-600 ">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-gray-400 ">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {x.options.drawTrack &&
                                                                            x.options.display ? (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer  text-gray-600">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                    <Icon className="w-4 h-4 cursor-pointer  text-gray-400">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className=" rawActionsRight no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                        <div>
                                                                            {!x.options.display ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplay(x.uuid, true);
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                        html={translate('show vessel')}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplay(
                                                                                            x.uuid,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                        html={translate('hide vessel')}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {x.options.drawTrack &&
                                                                            x.options.display ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrack(
                                                                                            x.uuid,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-track-tooltip`}
                                                                                        html={translate(
                                                                                            'hide vessel track'
                                                                                        )}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrack(
                                                                                            x.uuid,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-40"
                                                                                        id={`${x.uuid}-vessel-track-tooltip`}
                                                                                        html={translate(
                                                                                            'show vessel track'
                                                                                        )}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        );
                                                    })}
                                            </div>
                                        </MenuGroup>
                                    );
                                })}
                        </div>
                    </>
                )}
            </div>
        );
    }
}
const playbackTransitTabStateToProps = (state: RootState) => ({
    layout: state.layout.layout,
    port: state.port.port,
    mapControls: state.map.mapControls,
    switchs: state.layout.switchs,
    mainControls: state.layout.mainControls,
    openModelFunction: state.layout.openModelFunction,
    dateTimeFormat: state.port.dateTimeFormat,
    metOceanChartControls: state.layout.metOceanChartControls,
    user: state.user.user,
    vesselCategories: state.port.vesselCategories,
});

const playbackTransitTabDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
    setMenuControlsAction: bindActionCreators(setMenuControlsAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(playbackTransitTabStateToProps, playbackTransitTabDispatchToProps)(PlaybackTransitTab);
